import { computed, ref } from 'vue'
import PackGoSettingsService from '@/services/packgo/packgo-settings.service'
import { PACKGO_MODES } from '@/app/packgo/shared/constants'
import { defineStore } from 'pinia'
import type {
  PackGoFilterType,
  PackGoFiltersChoiceRequestPayload,
  PackGoItemPacking,
  PackGoSettings,
} from '@/types/packgo.models'
import type { RequestOptions, RequestStatus } from '@/types/api'
import i18n from '@/i18n/i18n'

type Statuses = {
  updateSettings?: RequestStatus
  fetchSettings?: RequestStatus
  updateDynamicFilters?: RequestStatus
}

const EMPTY_OBJECT = {}

export const ORDER_QUEUE_SETTINGS_FILTER_TYPES: PackGoFilterType[] = [
  {
    title: i18n.t('Please check the order status'),
    name: i18n.t('order status'),
    orderLabel: 'status',
    orderIdentifier: 'status_identifier',
    settingsIdentifier: 'order',
  },
  {
    title: i18n.t('Please check the payment status'),
    name: i18n.t('payment status'),
    orderLabel: 'payment_status',
    orderIdentifier: 'payment_status_identifier',
    settingsIdentifier: 'payment',
  },
]

export const usePackGoSettingsStore = defineStore('packGoSettings', () => {
  /**
   * STATE
   */
  const orderQueueSettings = ref<PackGoSettings | undefined>(undefined)
  const statuses = ref<Statuses>({})
  const filterTypes = ref(ORDER_QUEUE_SETTINGS_FILTER_TYPES)

  /**
   * GETTERS
   */
  const isModeSet = computed(() => Boolean(orderQueueSettings.value?.mode))
  const filters = computed(() => orderQueueSettings.value?.filters)
  const mode = computed(() => orderQueueSettings.value?.mode)
  const itemPackingSettings = computed(
    (): Partial<PackGoItemPacking> => orderQueueSettings.value?.item_packing ?? EMPTY_OBJECT,
  )
  const isAutoMode = computed(() => orderQueueSettings.value?.mode === PACKGO_MODES.AUTO)
  const isSingleScanMode = computed(() => Boolean(orderQueueSettings.value?.single_scan))
  const isItemPackingEnabled = computed(() => Boolean(orderQueueSettings.value?.item_packing?.enabled))
  const areItemImagesEnabled = computed(() => !orderQueueSettings.value?.hide_item_images)
  const requiresProcessDuplicateWarning = computed(() => Boolean(orderQueueSettings.value?.process_duplicate_warning))
  const requiresFilterMismatchConfirmation = computed(() =>
    Boolean(orderQueueSettings.value?.filter_mismatch_confirmation),
  )

  /**
   * ACTIONS
   */
  const fetchOrderQueueSettings = async ({ forceRefetch = false }: RequestOptions = {}) => {
    const isDoneOrInProgress = ['ok', 'pending'].includes(statuses.value.fetchSettings || '')
    if (!forceRefetch && isDoneOrInProgress) {
      return
    }
    try {
      statuses.value.fetchSettings = 'pending'
      orderQueueSettings.value = await PackGoSettingsService.getSettings()
      statuses.value.fetchSettings = 'ok'
    } catch (e) {
      statuses.value.fetchSettings = 'error'
      throw e
    }
  }
  const updateOrderQueueSettings = async (partialOrderQueueSettings: Partial<PackGoSettings>) => {
    try {
      statuses.value.updateSettings = 'pending'
      orderQueueSettings.value = await PackGoSettingsService.updateSettings(partialOrderQueueSettings)
      statuses.value.updateSettings = 'ok'
    } catch (e) {
      statuses.value.updateSettings = 'error'
      throw e
    }
  }

  const updateDynamicFilters = async (payload: PackGoFiltersChoiceRequestPayload) => {
    try {
      statuses.value.updateDynamicFilters = 'pending'
      await PackGoSettingsService.updateDynamicFilters(payload)
      await fetchOrderQueueSettings({ forceRefetch: true })
      statuses.value.updateDynamicFilters = 'ok'
    } catch (e) {
      statuses.value.updateDynamicFilters = 'error'
      throw e
    }
  }

  return {
    // STATE
    orderQueueSettings,
    statuses,
    filterTypes,
    // GETTERS
    isModeSet,
    filters,
    mode,
    itemPackingSettings,
    isAutoMode,
    isSingleScanMode,
    isItemPackingEnabled,
    areItemImagesEnabled,
    requiresProcessDuplicateWarning,
    requiresFilterMismatchConfirmation,
    // ACTIONS
    updateOrderQueueSettings,
    fetchOrderQueueSettings,
    updateDynamicFilters,
  }
})
